import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { DEFAULT_ORDER_EXPIRATION_DAYS, ORDER_MSG_VERSION } from '../constants/codex';
import { getRandomInt } from '../common/utils';
import { getCancelOrderDataJsonBytes, makeLoginMsg, makeTradingKeyMsg, makeCreateOrderMsg } from '../helpers/codex.helper';
import { makeWithdrawMsg } from '../helpers/withdraw.helper';
import { ICreateOrderData } from '../interfaces/order.interface';

import { SocketManager } from "./sockets";
import { AuthCredentials, ClientOptions, CancelOrderArgs, CreateOrderArgs, Signer, SubscribeOptions, TelegramData, WalletCredentials, UserNotification } from "./interfaces";
import { AlgodService } from "./algodService";
import { PRIVATE_STREAMS } from "./enums";
import { LocalStorageService } from "./localStorage";
import { ITradingKeyData, TradingKeyType } from "../interfaces/tradingKey.interface";
import { ILoginData, ITransferData, IWithdrawData, PROVIDERS } from "../interfaces/wallet.interface";
import { OrderStatus } from "../db/enums";
import { KYCAuthenticationStatus } from "../db/enums/account";
import { makeDtwMsg, makeTransferMsg } from "../helpers/codex";
import { CreateWithdrawalWallet, UpdateWithdrawalWallet } from "../interfaces/withdrawalWallets.interface";
import { ISafeWithdrawalWallets } from "../db/interfaces/withdrawal_wallets";

const DEFAULT_LOGIN_MESSAGE = `By signing this message you are logging into your trading account and agreeing to all terms and conditions of the platform.`;

export class Client {
  private client: AlgodService;
  private algodNode: string;
  private algodIndexer: string;
  private apiUrl: string;
  private companyId: number;
  private websocketUrl: string;
  private wallet: WalletCredentials | null;
  private _axios: AxiosInstance;
  private localStorageService: LocalStorageService;
  private isUltradeID: boolean;
  public socketManager: SocketManager;
  
  constructor(
    options: ClientOptions,
    authCredentials?: AuthCredentials,
  ) {

    if (options.network === "mainnet") {
      this.algodNode = 'https://mainnet-api.algonode.cloud';
      this.apiUrl = 'https://mainnet-api.algonode.cloud';
      this.algodIndexer = 'https://mainnet-idx.algonode.cloud';
    }
    else if (options.network === "testnet") {
      this.apiUrl = "https://testnet-apigw.ultradedev.net"
      this.algodNode = 'https://testnet-api.algonode.cloud';
      this.algodIndexer = 'https://testnet-idx.algonode.cloud';
    } else {
      this.apiUrl = "http://localhost:5001";
      this.algodNode = 'http://localhost:4001';
      this.algodIndexer = 'http://localhost:8980'
    }
    if (options.apiUrl !== undefined) {
      this.apiUrl = options.apiUrl;
    }
    if (options.companyId !== undefined) {
      this.companyId = options.companyId;
    }
    this.websocketUrl = options.websocketUrl;
    this.client = new AlgodService(options.algoSdkClient, authCredentials || {} as AuthCredentials, this.algodIndexer)

    this._axios = this.axiosInterceptor(axios.create({
      baseURL: this.apiUrl,
    }));
    this.localStorageService = new LocalStorageService();
    this.wallet = this.localStorageService.getMainWallet();
    this.isUltradeID = false;
    
    this.socketManager = new SocketManager(
      this.websocketUrl,
      options.socketIO,
      (socketId) => {
        console.log(`Socket ${socketId} disconnected at`, new Date());
      },
      (err) => {
        console.log(`Socket connect_error due to ${err}`);
      }
    );    
    console.log('SDK Wallet', this.wallet);
  }

  private axiosInterceptor = (axios: AxiosInstance): AxiosInstance => {
    const tokenizedUrls = [
      '/market/balances',
      '/market/order',
      '/market/orders',
      '/market/account/kyc/status',
      '/market/account/kyc/init',
      '/market/withdrawal-fee',
      '/market/operation-details',
      '/wallet/key',
      '/wallet/transactions',
      '/wallet/transfer',
      '/wallet/withdraw',
      '/wallet/whitelist',
      '/wallet/withdrawal-wallets',
    ];
    const tokenRequired = (config: AxiosRequestConfig<any>) => {
      return config.withWalletCredentials || tokenizedUrls.some(value => config.url.includes(value))
    };
    const tradingKeyRequired = (config: AxiosRequestConfig<any>) => {
      const urls = [
        '/market/order',
        // '/wallet/transfer',
      ];
      return config.withWalletCredentials || urls.some(value => config.url.includes(value))
    };
    const companyIdRequired = (config: AxiosRequestConfig<any>) => {
      const urls = [
        '/wallet/signin',
        '/market/account/kyc/init',
        '/notifications'
      ];
      return urls.some(value => config.url.includes(value))
    };
    const isSocialRequest = (config: AxiosRequestConfig<any>) => {
      const urls = [
        '/social/',
      ];
      return urls.some(value => config.url.includes(value))
    };
    const ifCollectStatistic = (config: AxiosRequestConfig<any>) => {
      return tokenizedUrls.some(value => config.url.includes(value))
    };

    axios.interceptors.request.use(
      (config: AxiosRequestConfig<any>) => {
        if (this.wallet && tokenRequired(config)) {
          config.headers['X-Wallet-Address'] = this.wallet.address;
          config.headers['X-Wallet-Token'] = this.wallet.token;
        }
        if (this.wallet && ifCollectStatistic(config)) {
          config.headers['CompanyId'] = this.companyId;
        }
        if (this.wallet && tradingKeyRequired(config)) {
          const tradingKey = this.wallet?.tradingKey;
          if (tradingKey) {
            config.headers['X-Trading-Key'] = tradingKey;
          }
        }
        if (companyIdRequired(config)) {
          config.headers['CompanyId'] = this.companyId;
        }
        if (isSocialRequest(config) && !config.headers.CompanyId) {
          config.headers['CompanyId'] = this.isUltradeID ? 1 : this.companyId;
        }
        return config;
      },
      (error: any) => Promise.reject(error),
    );
  
    //Add a response interceptor
    axios.interceptors.response.use(
      (response: AxiosResponse<any, any>) => {
        return response.data;
      },
      async (error: any) => {
        console.log('Request was failed', error);
        const invalidToken = [401].includes(
          error?.response?.status,
        );
        
        if (invalidToken && tokenRequired(error.config)) {
          this.wallet = null;
          this.localStorageService.clearMainWallet();
        }

        return Promise.reject(error);
      },
    );
  
    return axios;
  }

  // TODO: rework emit logic in SDK UL-1257

  // public setState(args: AppSocketState) {
  //   return ws.emitPair(args);
  // }

  // public setFilter(args: AppSocketState) {
  //   return ws.emitFilter(args);
  // }

  public get useUltradeID(): boolean {
    return this.isUltradeID;
  }

  public set useUltradeID(isUltrade: boolean) {
    this.isUltradeID = isUltrade;
  }

  public get isLogged() {
    return !!this.wallet?.token;
  }

  public get mainWallet(): WalletCredentials | null {
    return this.wallet;
  }

  public set mainWallet(wallet: WalletCredentials) {
    this.wallet = wallet;
    if (!wallet) {
      this.localStorageService.clearMainWallet();
    } else {
      this.localStorageService.setMainWallet(wallet);
    }
  }

  public setSigner(signer: Signer) {
    this.client.signer = signer;
  }

  public subscribe(subscribeOptions: SubscribeOptions, callback: Function): number {
    const requiresAuth = subscribeOptions.streams.some(stream => PRIVATE_STREAMS.includes(stream));

    if(requiresAuth && !this.mainWallet?.token && !this.mainWallet?.tradingKey) {
      subscribeOptions.streams = subscribeOptions.streams.filter(stream => !PRIVATE_STREAMS.includes(stream));
      return this.socketManager.subscribe(subscribeOptions, callback);
    }

    if(requiresAuth) {
      subscribeOptions.options = {
        ...subscribeOptions.options,
        token: this.mainWallet?.token,
        tradingKey: this.mainWallet?.tradingKey
      }
      return this.socketManager.subscribe(subscribeOptions, callback);
    }

    return this.socketManager.subscribe(subscribeOptions, callback);
  }

  public unsubscribe(handlerId: number) {
    this.socketManager.unsubscribe(handlerId);
  }

  public getPairList(companyId?: number) {
    const query = companyId ? `&companyId=${companyId}` : "";
    return this._axios.get(`/market/markets?includeAllOrders=false${query}`);
  }

  public getExchangeInfo(symbol: string | number) {
    return this._axios.get(`/market/market?symbol=${symbol}`);
  }

  public getPrice(symbol: string): Promise<{ ask: number, bid: number, last: number }> {
    return this._axios.get(`/market/price?symbol=${symbol}`);
  }

  public getDepth(symbol: string, depth: number): Promise<any> {
    return this._axios.get(`/market/depth?symbol=${symbol}&depth=${depth}`);
  }

  public getSymbols(mask?: string): Promise<Array<{pairKey: string}>> {
    return this._axios.get(`/market/symbols${mask ? '?mask=' + mask : ''}`);
  }

  public getLastTrades(symbol: string): Promise<any> {
    return this._axios.get(`/market/last-trades?symbol=${symbol}`);
  }

  public getHistory(symbol: string, interval: string, startTime?: number, endTime?: number, limit=500, page: number = 1): Promise<any> {
    return this._axios.get(`/market/history?symbol=${symbol}&interval=${interval}&startTime=${startTime ?? ""}&endTime=${endTime ?? ""}&limit=${limit}&page=${page}`);
  }

  public getOrders(symbol?: string, status?: number, limit = 50, endTime?: number, startTime?: number) {
    const statusStr = !status
      ? ""
      : (status === 1 )
        ? OrderStatus.Open
        : [OrderStatus.Canceled, OrderStatus.Matched, OrderStatus.SelfMatched, OrderStatus.Expired].join(',');

    const symbolQuery = symbol ? `&symbol=${symbol}` : "";
    const statusQuery = statusStr ? `&status=${statusStr}` : "";
    const startTimeQuery = startTime ? `&startTime=${startTime}` : "";
    const endTimeQuery = endTime ? `&endTime=${endTime}` : "";
    return this._axios.get(`/market/orders?limit=${limit}${symbolQuery}${statusQuery}${startTimeQuery}${endTimeQuery}`);
  }

  public getOrderById(orderId: number): Promise<any> {
    return this._axios.get(`/market/order/${orderId}`);
  }

  public getSettings(): Promise<any> {
    const domain = new URL((window.location !== window.parent.location) ? document.referrer : document.location.href).host;
    return this._axios.get(`/market/settings`, { headers: { "wl-domain": domain } });
  }

  public getBalances(): Promise<any> {
    return this._axios.get(`/market/balances`);
  }

  public getChains(): Promise<any> {
    return this._axios.get(`/market/chains`);
  }

  public getCodexAssets(): Promise<any> {
    return this._axios.get(`/market/assets`);
  }

  public getCCTPAssets(): Promise<any> {
    return this._axios.get(`/market/cctp-assets`);
  }

  public getCCTPUnifiedAssets(): Promise<any> {
    return this._axios.get(`/market/cctp-unified-assets`);
  }

  public getWithdrawalFee(assetAddress: string, chainId: number): Promise<any> {
    return this._axios.get(`/market/withdrawal-fee?assetAddress=${assetAddress}&chainId=${chainId}`);
  }
  
  public getKycStatus(): Promise<{ kycStatus?: KYCAuthenticationStatus }> {
    return this._axios.get(`/market/account/kyc/status`);
  }

  public getKycInitLink(embeddedAppUrl: string | null): Promise<{ url: string }> {
    return this._axios.post(`/market/account/kyc/init`, { embeddedAppUrl });
  }

  public getDollarValues(assetIds: number[] = []): Promise<{ [key: string]: number }> {
    return this._axios.get(`/market/dollar-price?assetIds=${JSON.stringify(assetIds)}`);
  }

  public getTransactionDetalis(transactionId: number): Promise<any> {
    return this._axios.get(`/market/operation-details?operationId=${transactionId}`);
  }

  //#region Wallet

  public getWalletTransactions(type: string, page: number, limit: number = 100) {
    return this._axios.get(`/wallet/transactions?type=${type}&limit=${limit}&page=${page}`, { withWalletCredentials: true });
  }

  public getTradingKeys() {
    return this._axios.get(`/wallet/keys`, { withWalletCredentials: true });
  }

  public getTransfers(page: number, limit: number = 100) {
    return this._axios.get(`/wallet/transfers?limit=${limit}&page=${page}`, { withWalletCredentials: true });
  }

  public getPendingTransactions() {
    return this._axios.get(`/wallet/transactions/pending`, { withWalletCredentials: true });
  }

  public getWhitelist() {
    return this._axios.get(`/wallet/whitelist`, { withWalletCredentials: true });
  }

  public addWhitelist(data) {
    const encoding = 'hex';
    data = {
      ...data,
      expiredDate: data.expiredDate && Math.round(data.expiredDate / 1000)
    }
    const message = Buffer.from(makeDtwMsg(data)).toString(encoding);
    return this.client.signAndSendData(
      message,
      this.client.signer.signMessage,
      ({ signature }) => this._axios.post(`/wallet/whitelist`, { message, signature }),
      encoding
    );
  }

  public deleteWhitelist(whitelistId: number) {
    const data = { whitelistId };
    return this.client.signAndSendData(
      data,
      this.client.signer.signMessage,
      ({ signature }) => this._axios.delete(`/wallet/whitelist`, {
        data: { data, signature }
      }),
    );
  }

  public getAllWithdrawalWallets(): Promise<ISafeWithdrawalWallets[]> {
    return this._axios.get(`/wallet/withdrawal-wallets`);
  }

  public getWithdrawalWalletByAddress(address: string): Promise<ISafeWithdrawalWallets> {
    return this._axios.get(`/wallet/withdrawal-wallets/${address}`);
  }

  public createWithdrawalWallet(body: CreateWithdrawalWallet): Promise<ISafeWithdrawalWallets> {
    return this._axios.post(`/wallet/withdrawal-wallets`, body);
  }

  public updateWithdrawalWallet(params: UpdateWithdrawalWallet): Promise<boolean> {
    return this._axios.patch(`/wallet/withdrawal-wallets`, params);
  }

  public deleteWithdrawalWallet(address: string): Promise<boolean> {
    return this._axios.delete(`/wallet/withdrawal-wallets/${address}`);
  }
  //#endregion Wallet

  //#region System

  public getVersion(): Promise<any> {
    return this._axios.get(`/system/version`);
  }

  public getMaintenance(): Promise<any> {
    return this._axios.get(`/system/maintenance`);
  }

  public getNotifications(): Promise<UserNotification[]> {
    return this._axios.get(`/notifications`, { withWalletCredentials: true });
  }

  public getNotificationsUnreadCount(): Promise<{ count: number }> {
    return this._axios.get(`/notifications/count`, { withWalletCredentials: true });
  }

  public readNotifications(notifications: any[]): Promise<any> {
    return this._axios.put(`/notifications`, { notifications }, { withWalletCredentials: true })
  }

  //#endregion System

  //#region Affiliates

  public getAffiliatesStatus(companyId: number) {
    return this._axios.get(`/affiliates/${companyId}/dashboardStatus`, { withWalletCredentials: true })
  }

  public createAffiliate(companyId: number) {
    return this._axios.post(`/affiliates/${companyId}`, {}, { withWalletCredentials: true })
  }

  public getAffiliateProgress(companyId: number) {
    return this._axios.get(`/affiliates/${companyId}/tradingVolumeProgress`, { withWalletCredentials: true })
  }

  public getAffiliateInfo(companyId: number, range: string) {
    return this._axios.get(`/affiliates/${companyId}/dashboard?range=${range}`, { withWalletCredentials: true })
  }

  public countAffiliateDepost(companyId: number) {
    return this._axios.post(`/affiliates/${companyId}/deposit `, {}, { withWalletCredentials: true })
  }

  public countAffiliateClick(referralToken: string) {
    return this._axios.post(`/affiliates/click`, { referralToken }, { withWalletCredentials: true })
  }

  //#endregion Affiliates

  //#region Social
  public getSocialAccount() {
    return this._axios.get(`/social/account`, { withWalletCredentials: true })
  }

  public addSocialEmail(email: string, embeddedAppUrl: string) {
    return this._axios.put(`/social/account/email`, { email, embeddedAppUrl }, { withWalletCredentials: true })
  }

  public verifySocialEmail(email: string, hash: string) {
    return this._axios.put(`/social/account/verifyEmail`, { email, hash }, { withWalletCredentials: true })
  }

  public getLeaderboards() {
    return this._axios.get(`/social/leaderboard`, { withWalletCredentials: true });
  }

  public getUnlocks() {
    return this._axios.get(`/social/unlocks`, { withWalletCredentials: true });
  }

  public getSocialSettings() {
    return this._axios.get(`/social/settings`, { withWalletCredentials: true });
  }

  public getSeason(ultradeId?: number) {
    return this._axios.get(`/social/seasons/active`, { 
      withWalletCredentials: true,
      headers: { "CompanyId": ultradeId } 
    });
  }

  public getPastSeasons() {
    return this._axios.get(`/social/seasons/history`, { withWalletCredentials: true });
  }

  //#region Telegram
  public addTelegram(data: TelegramData) {
    return this._axios.post(`/social/telegram/connect`, data, { withWalletCredentials: true })
  }

  public disconnectTelegram(data: TelegramData) {
    return this._axios.put(`/social/telegram/disconnect`, data, { withWalletCredentials: true })
  }

  //#endregion Telegram

  //#region Discord
  public getDiscordConnectionUrl(url) {
    const query = url ? `?embeddedAppUrl=${encodeURIComponent(url)}` : "";
    return this._axios.get(`/social/discord/connect${query}`, { withWalletCredentials: true })
      .then(({ url }) => url);
  }

  public disconnectDiscord() {
    return this._axios.put(`/social/discord/disconnect`, {}, { withWalletCredentials: true })
  }
  //#endregion Discord

  //#region Twitter
  public getTwitterConnectionUrl(appUrl: string, permissions?: string) {
    const queryURL = appUrl ? `?embeddedAppUrl=${encodeURIComponent(appUrl)}` : "";
    const queryPermissions = permissions ? `&scopes=${permissions}` : "";
    return this._axios.get(`/social/twitter/connect${queryURL}${queryPermissions}`, { withWalletCredentials: true })
      .then(({ url }) => url);
  }

  public disconnectTwitter() {
    return this._axios.put(`/social/twitter/disconnect`, {}, { withWalletCredentials: true });
  }

  public getTweets() {
    return this._axios.get(`/social/twitter/tweets`, { withWalletCredentials: true });
  }
  
  public actionWithTweet(data: { actions: [{id: number, text?: string}], tweetId?: string }) {
    return this._axios.post(`/social/twitter/tweet/actions`, data, { withWalletCredentials: true });
  }
  
  public async getActions() {
    return this._axios.get(`/social/actions`, { withWalletCredentials: true })
  }

  public async getActionHistory() {
    return this._axios.get(`/social/actions/history`, { withWalletCredentials: true })
  }

  public getAIStyles() {
    return this._axios.get(`/social/twitter/tweets/styles`, { withWalletCredentials: true });
  }

  public getAIComment(styleId: number, tweetId: string) {
    return this._axios.get(`/social/twitter/tweets/${tweetId}/generateComment?styleId=${styleId}`, { withWalletCredentials: true });
  }
  //#endregion Twitter

  //#endregion Social

  private getTechnologyByProvider(provider: PROVIDERS) {
    switch (provider) {
      case PROVIDERS.PERA:
        return "ALGORAND";
      case PROVIDERS.METAMASK:
        return "EVM";
      case PROVIDERS.SOLFLARE:
      case PROVIDERS.COINBASE:
      case PROVIDERS.PHANTOM:
      case PROVIDERS.BACKPACK:
      case PROVIDERS.MOBILE:
        return "SOLANA";

      default: throw new Error("Not implemented");
    }
  }

  public login({ address, provider, chain, referralToken, loginMessage }: ILoginData) {
    const encoding = 'hex';
    const signingMessage = loginMessage || DEFAULT_LOGIN_MESSAGE;
    const data = {
      address,
      technology: this.getTechnologyByProvider(provider),
    };
    const message = Buffer.from(makeLoginMsg(data, signingMessage)).toString(encoding);
    return this.client.signAndSendData(
      message,
      this.client.signer.signMessage,
      ({ signature }) => this._axios.put(`/wallet/signin`, { data, message, encoding, signature, referralToken })
        .then((token) => {
          this.mainWallet = { address, provider, token, chain };
          return token;
        }),
      encoding
    );
  }

  public addTradingKey(data: ITradingKeyData) {
    const encoding = 'hex';
    const message = Buffer.from(makeTradingKeyMsg(data, true)).toString(encoding);
    const { device, type } = data;
    return this.client.signAndSendData(
      message,
      this.client.signer.signMessage,
      ({ signature }) => this._axios.post(`/wallet/key`, {
        data: { device, type }, encoding, message, signature
      })
        .then((tradingKey: any) => {
          if (this.mainWallet && tradingKey.type === TradingKeyType.User) {
            this.mainWallet.tradingKey = tradingKey.address;
          }
          return tradingKey;
        }),
      encoding
    )
  }

  public revokeTradingKey(data: ITradingKeyData) {
    const encoding = 'hex';
    const message = Buffer.from(makeTradingKeyMsg(data, false)).toString(encoding);
    const { device, type } = data;
    return this.client.signAndSendData(
      message,
      this.client.signer.signMessage,
      ({ signature }) => this._axios.delete(`/wallet/key`, {
        data: { data: { device, type }, encoding, message, signature }
      })
      .then(() => {
        if (this.mainWallet && data.tkAddress === this.mainWallet.tradingKey) {
          this.mainWallet.tradingKey = undefined;
        }
      }),
      encoding
    );
  }

  public withdraw(withdrawData: IWithdrawData, prettyMsg?: string) {
    const encoding = 'hex';
    const data = {
      ...withdrawData,
      random: getRandomInt(1, Number.MAX_SAFE_INTEGER),
    };
    const message = Buffer.from(makeWithdrawMsg(data, prettyMsg)).toString(encoding);
    return this.client.signAndSendData(
      message,
      this.client.signer.signMessage,
      ({ signature }) => this._axios.post(`/wallet/withdraw`, { encoding, message, signature, destinationAddress: data.recipient }),
      encoding
    );
  }

  public transfer(transferData: ITransferData) {
    const encoding = 'hex';
    const data = {
      ...transferData,
      random: getRandomInt(1, Number.MAX_SAFE_INTEGER),
    }
    const message = Buffer.from(makeTransferMsg(data)).toString(encoding);
    return this.client.signAndSendData(
      message,
      this.client.signer.signMessage,
      ({ signature }) => this._axios.post(`/wallet/transfer`, { message, signature }),
      encoding
    );
  }

  public async createOrder(order: CreateOrderArgs) {
    const _daysInSec = DEFAULT_ORDER_EXPIRATION_DAYS * 24 * 60 * 60;
    const expiredTime = Math.floor(Date.now() / 1000) + _daysInSec;
    const data: ICreateOrderData = {
      ...order,
      version: ORDER_MSG_VERSION,
      expiredTime,
      random: getRandomInt(1, Number.MAX_SAFE_INTEGER),
    };
    console.log('CreateOrderData', data);
    const encoding = 'hex';
    const message = Buffer.from(makeCreateOrderMsg(data)).toString(encoding);

    return await this.client.signAndSendData(
      message,
      this.client.signer.signMessageByToken,
      ({ signature }) => this._axios.post(`/market/order`, { encoding, message, signature }),
      encoding
    );
  }

  public async cancelOrder(order: CancelOrderArgs): Promise<any> {
    const data = { orderId: order.orderId };
    const encoding = 'hex';
    const message = Buffer.from(getCancelOrderDataJsonBytes(order)).toString(encoding);
    return this.client.signAndSendData(
      message,
      this.client.signer.signMessageByToken,
      ({ signature }) => this._axios.delete(`/market/order`, {
        data: { data, message, signature }
      }),
      encoding
    );
  }

  public async cancelMultipleOrders({ orderIds, pairId }: { orderIds?: number[], pairId?: number }): Promise<any> {
    const data = { orderIds, pairId };
    return this.client.signAndSendData(
      data,
      this.client.signer.signMessageByToken,
      ({ signature }) => this._axios.delete(`/market/orders`, {
        data: { data, signature }
      }),
    );
  }

  public async ping() {
    const data = this._axios.get(`/system/time`)
    return Math.round(Date.now() - data.currentTime)
  }
}
